import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { useForm } from '@mantine/form';
import {
    Button,
    SegmentedControl,
    Textarea,
    Text,
    Badge,
    NumberInput,
    Flex,
    Checkbox,
    Accordion,
    NativeSelect,
    Collapse,
    Stack,
    Grid,
    Box,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCalendar, IconMapPin } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DatePickerInput } from '@mantine/dates';

import { Request, Store } from '../../codegen/graphql';
import { useAppConfig } from '../../hooks/useAppConfig';
import MarginCalculation from './components/MarginCalculation';
import { StoreSelectionItem } from './components/StoreSelectionItem';
import { PriceChangeRequestMetadata } from '../../types/price-change-request';
import { getExVatPrice } from '../../helpers/calculateMargin';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { getRequestEffectiveCostPrice } from '../../helpers/requestZonePricing';
import {
    getHighestCashbuildCostPrice,
    getHighestCostPrice,
    getHighestPnlCostPrice,
} from '../../helpers/priceCalculation';
import { getSuggestedStoreIds } from '../../helpers/priceChangeScope';
import {
    shouldDisablePnlPriceInput,
    shouldDisableCashbuildPriceInput,
    shouldShowCashbuildPriceInput,
    shouldShowPnlPriceInput,
    determineRequiredPriceTypes,
} from '../../helpers/storeHelpers';
import { isStoreSelected } from '../../helpers/storeSelectionHelper';
import { useHighestCostPriceStore } from '../../hooks/useHighestCostPriceStore';
import { useEligibleStoresCount } from '../../hooks/useEligibleStoresCount';

const APPLICABLE_TO_OPTIONS = [
    { value: 'zone', label: 'Advertising Zone' },
    { value: 'store', label: 'Store' },
];

type Props = {
    isDecline: boolean;
    request: Request & { resolvedVatPercentage?: number | null };
    store: Store | null;
    advertisingZoneStores: Store[];
    close: () => void;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: PriceChangeRequestMetadata;
    }) => void;
};

function SupplierFeedbackForm({
    isDecline,
    request,
    store,
    advertisingZoneStores,
    onConfirm,
    close,
}: Props) {
    const { supplierDeclineReasons, vatPercentage: fallbackVatPercentage } =
        useAppConfig();
    const resolvedVatPercentage =
        request?.resolvedVatPercentage ?? fallbackVatPercentage;

    const stores = useMemo(() => {
        const filteredStores =
            advertisingZoneStores?.filter(s => !s.isClosed) || [];
        let allStores: typeof filteredStores = [];
        if (filteredStores.length > 0) {
            allStores = filteredStores;
        } else if (store) {
            allStores = [store];
        }
        return allStores;
    }, [advertisingZoneStores, store]);

    const initialApplicableTo =
        advertisingZoneStores.length > 0 ? 'zone' : 'store';
    const initialSelectedStores = useMemo(() => {
        const liveStoreIds = stores
            .filter(zoneStore => zoneStore?.productAvailability?.isLive)
            .map(zoneStore => zoneStore.id);
        const suggested = getSuggestedStoreIds(request).filter(storeId =>
            liveStoreIds.includes(storeId),
        );
        if (suggested.length > 0) {
            return suggested;
        }
        return liveStoreIds;
    }, [request, stores]);

    const selectionRef = useRef({
        stores,
        store,
        fallbackCostPrice: null as number | null,
    });
    useEffect(() => {
        selectionRef.current = {
            stores,
            store,
            fallbackCostPrice: getRequestEffectiveCostPrice(request ?? {}),
        };
    }, [request, store, stores]);
    const costForSelection = useCallback(
        (applicableTo: string, selectedIds: string[]) => {
            const { current } = selectionRef;
            const {
                store: selectedStore,
                stores: zoneStores,
                fallbackCostPrice,
            } = current;
            let selected: Store[] = [];
            if (applicableTo === 'store') {
                if (selectedStore) {
                    selected = [selectedStore];
                }
            } else {
                selected = zoneStores.filter(zoneStore =>
                    selectedIds.includes(zoneStore.id),
                );
            }
            return getHighestCostPrice(selected) ?? fallbackCostPrice ?? 0;
        },
        [],
    );

    const form = useForm<{
        applicableTo: string;
        newPrice: number;
        cashbuildPrice: number;
        pnlPrice: number;
        notes: string;
        reason: string;
        adjustPrice: boolean;
        priceChangeDurationStart: Date | null;
        priceChangeDurationEnd: Date | null;
        noEndDate: boolean;
        immediatePriceChange: boolean;
        selectedStores: string[];
    }>({
        initialValues: {
            applicableTo: initialApplicableTo,
            newPrice:
                request?.temporaryCashbuildPrice ??
                request?.temporaryPnlPrice ??
                request?.proposedPrice,
            cashbuildPrice:
                request?.temporaryCashbuildPrice ?? request?.proposedPrice,
            pnlPrice: request?.temporaryPnlPrice ?? request?.proposedPrice,
            notes: '',
            reason: supplierDeclineReasons[0] ?? '',
            adjustPrice: false,
            priceChangeDurationStart: request?.proposedValidFrom
                ? new Date(request.proposedValidFrom)
                : dayjs().add(1, 'day').toDate(),
            priceChangeDurationEnd: null,
            noEndDate: false,
            immediatePriceChange: false,
            selectedStores: initialSelectedStores,
        },
        validate: {
            reason: value => {
                if (!value && isDecline) {
                    return 'Reason is required';
                }
                return null;
            },
            newPrice: (value: number, values) => {
                if (!isDecline || (isDecline && !values.adjustPrice)) {
                    return null;
                }

                return getExVatPrice(value, resolvedVatPercentage) >=
                    costForSelection(values.applicableTo, values.selectedStores)
                    ? null
                    : 'New price (ex VAT) must be more than the cost price.';
            },
            pnlPrice: (value, values) => {
                if (value === undefined || value === null) return null;
                if (
                    store?.isTradingAsCashbuild ||
                    shouldDisablePnlPriceInput(advertisingZoneStores)
                ) {
                    return null;
                }
                return getExVatPrice(value, resolvedVatPercentage) >=
                    costForSelection(values.applicableTo, values.selectedStores)
                    ? null
                    : 'P&L price (ex VAT) must be more than the cost price.';
            },
            priceChangeDurationStart: (value: Date | null, values) => {
                if (!isDecline || (isDecline && !values.adjustPrice)) {
                    return null;
                }

                if (!value) {
                    return 'Start date is required.';
                }
                return null;
            },
            priceChangeDurationEnd: (value: Date | null, values) => {
                if (!isDecline || (isDecline && !values.adjustPrice)) {
                    return null;
                }

                if (!value) {
                    return 'End date is required.';
                }
                return null;
            },
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['newPrice'],
    });

    const selectedStoresForCost = useMemo(() => {
        if (form.values.applicableTo === 'store') {
            return store ? [store] : [];
        }
        return stores.filter(zoneStore =>
            form.values.selectedStores.includes(zoneStore.id),
        );
    }, [form.values.applicableTo, form.values.selectedStores, store, stores]);
    const fallbackCostPrice = getRequestEffectiveCostPrice(request ?? {});
    const cashbuildCostPrice =
        getHighestCashbuildCostPrice(selectedStoresForCost) ??
        fallbackCostPrice;
    const pnlCostPrice =
        getHighestPnlCostPrice(selectedStoresForCost) ?? fallbackCostPrice;

    useEffect(() => {
        if (supplierDeclineReasons.length > 0 && !form.values.reason) {
            form.setFieldValue('reason', supplierDeclineReasons[0]);
        }
    }, [supplierDeclineReasons]);

    const highestCostPriceStoreId = useHighestCostPriceStore(
        advertisingZoneStores,
    );
    const totalEligible = useEligibleStoresCount(advertisingZoneStores);

    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    const handleClose = () => {
        form.reset();
        close();
    };

    const getMetadata = () => {
        if (form.values.adjustPrice || !isDecline) {
            let selectedStoresList: Store[] = [];
            if (advertisingZoneStores.length > 0) {
                selectedStoresList = advertisingZoneStores.filter(s =>
                    form.values.selectedStores.includes(s.id),
                );
            } else if (store) {
                selectedStoresList = [store];
            }

            const { needsCashbuildPrice, needsPnlPrice } =
                determineRequiredPriceTypes(selectedStoresList);

            const metadata: PriceChangeRequestMetadata = {
                cashbuildPrice: needsCashbuildPrice
                    ? Number(form.values.cashbuildPrice)
                    : undefined,
                pnlPrice: needsPnlPrice
                    ? Number(form.values.pnlPrice)
                    : undefined,
                changedPriceValidFrom: dayjs(
                    form.values.priceChangeDurationStart || new Date(),
                ).toISOString(),
                changedPriceValidTo: dayjs(
                    form.values.priceChangeDurationEnd || new Date(),
                ).toISOString(),
                applyImmediately: form.values.immediatePriceChange,
                applyToStores: form.values.selectedStores,
            };

            if (isDecline) {
                metadata.reason = form.values.reason;
            }

            return metadata;
        }

        return {
            reason: form.values.reason,
        };
    };

    const openConfirmZoneApplicationModal = () => {
        modals.openConfirmModal({
            id: 'confirm-zone-application',
            title: 'Apply Change To the Advertising Zone?',
            modalId: 'confirm-zone-application',
            children: (
                <div className="p-2">
                    <Text>
                        Are you sure you want to apply this change request to
                        all the selected stores in the advertising zone?
                    </Text>
                </div>
            ),
            onConfirm: () => {
                onConfirm({
                    outcome: isDecline ? 'Declined' : 'Approved',
                    notes: form.values.notes,
                    metadata: getMetadata(),
                });
                handleClose();
            },

            classNames: {
                title: 'text-primary font-semibold text-lg',
                body: 'p-4',
                inner: 'z-[1200]',
                overlay: 'z-[1000]',
            },
            labels: {
                confirm: 'Confirm',
                cancel: 'Cancel',
            },

            confirmProps: {
                fullWidth: false,
                size: 'md',
            },
            cancelProps: {
                variant: 'outline',
                fullWidth: false,
                size: 'md',
            },
        });
    };

    const onChangeSelectedStore = (storeId: string, selected: boolean) => {
        const selectedStores = form.values.selectedStores || [];
        if (selected) {
            form.setFieldValue(
                'selectedStores',
                selectedStores.concat(storeId),
            );
        } else {
            form.setFieldValue(
                'selectedStores',
                selectedStores.filter(id => id !== storeId),
            );
        }
    };

    const renderDynamicContent = useCallback(() => {
        const applicableToStore = form.values.applicableTo === 'store';
        return (
            <div className="flex flex-col gap-2">
                {applicableToStore ? (
                    <>
                        <Text>Applying to Store</Text>
                        <div className="flex gap-2 items-center">
                            <IconMapPin size={18} />
                            <Text className="text-[#4D5E80] Capitalize">
                                {store?.name || request?.storeName || 'N/A'}
                            </Text>
                            <Badge size="sm" variant="light">
                                {store?.storeNo || request?.storeNo || 'N/A'}
                            </Badge>
                        </div>
                    </>
                ) : (
                    <Accordion
                        styles={{
                            control: {
                                paddingLeft: 0,
                                paddingRight: 0,
                            },
                            item: {
                                border: 'none',
                            },
                        }}
                    >
                        <Accordion.Item value="collapsible">
                            <Accordion.Control styles={{}}>
                                <Text>
                                    {form.values.selectedStores.length ===
                                    totalEligible
                                        ? `All ${form.values.selectedStores.length}`
                                        : `Selected ${form.values.selectedStores.length}`}{' '}
                                    stores in Advertising Zone
                                </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                {advertisingZoneStores?.map(s => (
                                    <StoreSelectionItem
                                        key={s.id}
                                        store={s}
                                        isSelected={isStoreSelected(
                                            s.id,
                                            form.values.selectedStores,
                                        )}
                                        onToggle={onChangeSelectedStore}
                                        isHighestCostPrice={
                                            s.id === highestCostPriceStoreId
                                        }
                                    />
                                ))}
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                )}
            </div>
        );
    }, [
        form.values,
        store,
        request,
        advertisingZoneStores,
        highestCostPriceStoreId,
    ]);

    const onChangeNoEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('noEndDate', event.currentTarget.checked);
        form.setFieldValue(
            'priceChangeDurationEnd',
            // @ts-ignore
            event.currentTarget.checked ? dayjs('9999-12-31').toDate() : null,
        );
    };

    const handleSubmit = form.onSubmit(values => {
        try {
            if (values.applicableTo === 'zone') {
                openConfirmZoneApplicationModal();
                return;
            }
            onConfirm({
                outcome: isDecline ? 'Declined' : 'Approved',
                notes: form.values.notes,
                metadata: getMetadata(),
            });

            handleClose();
        } catch (error: any) {
            form.setFieldError('notes', error.message);
        }
    });

    const renderPriceAdjustment = () => (
        <>
            {advertisingZoneStores.length > 0 && (
                <>
                    <Text color="#212529">Adjustment Applicable To</Text>
                    <SegmentedControl
                        data={APPLICABLE_TO_OPTIONS}
                        fullWidth
                        color="primary"
                        classNames={{
                            root: 'mt-1',
                        }}
                        {...form.getInputProps('applicableTo')}
                    />
                </>
            )}
            {!!form.errors?.applicableTo && (
                <Text color="#212529" className="mt-2 text-red-500 text-sm">
                    {form.errors.applicableTo}
                </Text>
            )}
            {form.values.applicableTo === 'zone' &&
                advertisingZoneStores.length === 0 && (
                    <Text size="sm" c="dimmed" mt={4}>
                        {request?.storeNo?.startsWith('P')
                            ? `Applying to Store: ${request.storeName} (${request.storeNo})`
                            : 'No stores in Advertising Zone'}
                    </Text>
                )}
            {renderDynamicContent()}
            <Grid>
                {(() => {
                    const isStoreLevel = form.values.applicableTo === 'store';
                    const isCashbuildDisabled = isStoreLevel
                        ? shouldDisableCashbuildPriceInput({ store })
                        : !form.values.selectedStores.some(id => {
                              const s = advertisingZoneStores.find(
                                  st => st.id === id,
                              );
                              return (
                                  !s?.storeNo?.startsWith('P') ||
                                  s?.isTradingAsCashbuild
                              );
                          });

                    const isPnlDisabled = isStoreLevel
                        ? !(store?.storeNo?.startsWith('P') ?? false) ||
                          (store?.isTradingAsCashbuild ?? false)
                        : shouldDisablePnlPriceInput(
                              advertisingZoneStores.filter(s =>
                                  form.values.selectedStores.includes(s.id),
                              ),
                          );

                    const shouldShowCashbuild = isStoreLevel
                        ? !isCashbuildDisabled
                        : shouldShowCashbuildPriceInput(advertisingZoneStores);

                    const shouldShowPnl = isStoreLevel
                        ? !isPnlDisabled
                        : shouldShowPnlPriceInput(advertisingZoneStores);

                    const showSingleInput =
                        (isStoreLevel &&
                            (isCashbuildDisabled || isPnlDisabled)) ||
                        (!isStoreLevel &&
                            (!shouldShowCashbuild || !shouldShowPnl));

                    return (
                        <>
                            {shouldShowCashbuild && (
                                <Grid.Col span={showSingleInput ? 12 : 6}>
                                    <NumberInput
                                        label="New Cashbuild Price"
                                        placeholder="Enter Cashbuild price"
                                        required
                                        min={0}
                                        step={0.1}
                                        decimalScale={2}
                                        disabled={
                                            !isStoreLevel && isCashbuildDisabled
                                        }
                                        rightSection={
                                            <Text className="pr-8 pl-2">
                                                {productCurrency}
                                            </Text>
                                        }
                                        {...form.getInputProps('newPrice')}
                                    />
                                    <Box mt="md">
                                        <MarginCalculation
                                            costPrice={cashbuildCostPrice}
                                            currentSellingPrice={
                                                request?.currentPrice ?? 0
                                            }
                                            proposedPrice={form.values.newPrice}
                                            benchmarkMargin={
                                                request?.benchmarkMargin ?? 0
                                            }
                                            currency={productCurrency}
                                            completed={request?.completed}
                                            previousCostPrice={
                                                request?.previousCostPrice
                                            }
                                            previousSellingPrice={
                                                request?.previousSellingPrice
                                            }
                                            previousCashbuildPrice={
                                                request?.previousCashbuildPrice
                                            }
                                            previousPnlPrice={
                                                request?.previousPnlPrice
                                            }
                                            priceType="cashbuild"
                                            layout={
                                                showSingleInput
                                                    ? 'row'
                                                    : 'column'
                                            }
                                            vatPercentage={
                                                resolvedVatPercentage
                                            }
                                        />
                                    </Box>
                                </Grid.Col>
                            )}

                            {shouldShowPnl && (
                                <Grid.Col span={showSingleInput ? 12 : 6}>
                                    <NumberInput
                                        label="New P&L Price"
                                        placeholder="Enter P&L price"
                                        min={0}
                                        step={0.1}
                                        decimalScale={2}
                                        disabled={
                                            !isStoreLevel && isPnlDisabled
                                        }
                                        rightSection={
                                            <Text className="pr-8 pl-2">
                                                {productCurrency}
                                            </Text>
                                        }
                                        {...form.getInputProps('pnlPrice')}
                                    />
                                    <Box mt="md">
                                        <MarginCalculation
                                            costPrice={pnlCostPrice}
                                            currentSellingPrice={
                                                request?.currentPrice ?? 0
                                            }
                                            proposedPrice={
                                                form.values.pnlPrice || 0
                                            }
                                            benchmarkMargin={
                                                request?.benchmarkMargin ?? 0
                                            }
                                            currency={productCurrency}
                                            completed={request?.completed}
                                            previousCostPrice={
                                                request?.previousCostPrice
                                            }
                                            previousSellingPrice={
                                                request?.previousSellingPrice
                                            }
                                            previousCashbuildPrice={
                                                request?.previousCashbuildPrice
                                            }
                                            previousPnlPrice={
                                                request?.previousPnlPrice
                                            }
                                            priceType="pnl"
                                            layout={
                                                showSingleInput
                                                    ? 'row'
                                                    : 'column'
                                            }
                                            vatPercentage={
                                                resolvedVatPercentage
                                            }
                                        />
                                    </Box>
                                </Grid.Col>
                            )}
                        </>
                    );
                })()}
            </Grid>
            <Stack>
                <Flex gap={18}>
                    <DatePickerInput
                        size="md"
                        radius="lg"
                        required
                        minDate={new Date()}
                        label="Price Start Date"
                        popoverProps={{
                            withinPortal: true,
                            zIndex: 999,
                            position: 'top',
                        }}
                        placeholder="Pick start date"
                        className="flex-1"
                        leftSection={<IconCalendar size={18} />}
                        {...form.getInputProps('priceChangeDurationStart')}
                    />
                    <Flex direction="column" gap={12} className="flex-1">
                        <DatePickerInput
                            size="md"
                            radius="lg"
                            required
                            minDate={
                                dayjs(form.values.priceChangeDurationStart)
                                    .add(1, 'day')
                                    .toDate() || new Date()
                            }
                            label="Price End Date"
                            popoverProps={{
                                withinPortal: true,
                                zIndex: 999,
                                position: 'top',
                            }}
                            disabled={form.values.noEndDate}
                            placeholder="Pick end date"
                            leftSection={<IconCalendar size={18} />}
                            {...form.getInputProps('priceChangeDurationEnd')}
                        />
                        <Checkbox
                            label="No end date"
                            {...form.getInputProps('noEndDate')}
                            onChange={onChangeNoEndDate}
                        />
                    </Flex>
                </Flex>
            </Stack>
        </>
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-4">
            <div>
                {isDecline && (
                    <>
                        <NativeSelect
                            data={supplierDeclineReasons}
                            label="Reason For Declining"
                            placeholder="Select a reason"
                            required
                            {...form.getInputProps('reason')}
                        />
                        <Checkbox
                            className="mt-5"
                            label="Adjust Price"
                            {...form.getInputProps('adjustPrice')}
                        />
                    </>
                )}
                {isDecline ? (
                    <Collapse in={form.values.adjustPrice} className="mt-5">
                        {renderPriceAdjustment()}
                    </Collapse>
                ) : (
                    renderPriceAdjustment()
                )}
            </div>
            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />

            {dayjs(form.values.priceChangeDurationStart).isSame(
                dayjs(),
                'day',
            ) && (
                <Checkbox
                    label="Apply price change immediately"
                    {...form.getInputProps('immediatePriceChange')}
                />
            )}

            <div className="flex gap-4">
                <Button size="md" variant="outline" onClick={handleClose}>
                    Cancel
                </Button>
                <Button size="md" type="submit">
                    Proceed
                </Button>
            </div>
        </form>
    );
}

export default SupplierFeedbackForm;
